import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiKeyDto } from '@today/types'
import { useState } from 'react'
import { apiMessage } from '@/hooks/offers/api-message'
import { apiKeysService } from '@/services/api-keys.service'

/** The list's cache key. Only `ApiKeyDto`s live under it — never a plaintext key. */
export const API_KEYS_QUERY_KEY = ['api-keys', 'me'] as const

/** The lifetime the create dialog proposes (TO-516 REQ-002 default). */
export const DEFAULT_LIFETIME_DAYS = 30

export const NAME_INVALID = 'Enter a name of 1 to 100 characters.'
export const DAYS_INVALID = 'Enter a whole number of days from 1 to 90.'

/**
 * The trimmed name when it has 1–100 Unicode code points, else undefined
 * (TO-516 REQ-002) — code points, as the API counts them, not UTF-16 units.
 */
export const parseApiKeyName = (text: string): string | undefined => {
	const name = text.trim()
	const codePoints = [...name].length
	return codePoints >= 1 && codePoints <= 100 ? name : undefined
}

/** A whole number of days 1–90, else undefined (TO-516 REQ-002). */
export const parseLifetimeDays = (text: string): number | undefined => {
	const trimmed = text.trim()
	if (!/^\d+$/.test(trimmed)) return undefined
	const days = Number(trimmed)
	return days >= 1 && days <= 90 ? days : undefined
}

/** The create body when the draft is valid, else each field's refusal. */
const validateCreateDraft = (name: string, days: string) => {
	const validName = parseApiKeyName(name)
	const expiresInDays = parseLifetimeDays(days)
	return {
		nameError: validName === undefined ? NAME_INVALID : null,
		daysError: expiresInDays === undefined ? DAYS_INVALID : null,
		body: validName !== undefined && expiresInDays !== undefined ? { name: validName, expiresInDays } : null,
	}
}

const errorText = (error: unknown, fallback: string): string | null => (error ? (apiMessage(error) ?? fallback) : null)

/** The create dialog: its draft, refusals and submit. */
function useCreateDraft(onCreated: (plaintextKey: string) => void, refresh: () => void) {
	const [isOpen, setOpen] = useState(false)
	const [name, setName] = useState('')
	const [days, setDays] = useState(String(DEFAULT_LIFETIME_DAYS))
	const [nameError, setNameError] = useState<string | null>(null)
	const [daysError, setDaysError] = useState<string | null>(null)

	const mutation = useMutation({
		// The plaintext is split off here and handed to component state only: the
		// mutation's result (kept in the mutation cache) never carries it.
		mutationFn: async (body: { name: string; expiresInDays: number }): Promise<ApiKeyDto> => {
			const { plaintextKey, ...key } = await apiKeysService.create(body)
			onCreated(plaintextKey)
			return key
		},
		onSuccess: () => {
			setOpen(false)
			refresh()
		},
	})

	return {
		isOpen,
		name,
		days,
		nameError,
		daysError,
		error: errorText(mutation.error, 'The key could not be created. Try again.'),
		isBusy: mutation.isPending,
		open: () => {
			mutation.reset()
			setName('')
			setDays(String(DEFAULT_LIFETIME_DAYS))
			setNameError(null)
			setDaysError(null)
			setOpen(true)
		},
		cancel: () => setOpen(false),
		setName: (text: string) => {
			setName(text)
			setNameError(null)
		},
		setDays: (text: string) => {
			setDays(text)
			setDaysError(null)
		},
		submit: () => {
			const draft = validateCreateDraft(name, days)
			setNameError(draft.nameError)
			setDaysError(draft.daysError)
			if (draft.body) mutation.mutate(draft.body)
		},
	}
}

/** The rename dialog: the key being renamed, its draft name and submit. */
function useRenameDraft(refresh: () => void) {
	const [target, setTarget] = useState<ApiKeyDto | null>(null)
	const [name, setName] = useState('')
	const [nameError, setNameError] = useState<string | null>(null)
	const mutation = useMutation({
		mutationFn: (input: { apiKeyId: string; name: string }) => apiKeysService.rename(input.apiKeyId, input.name),
		onSuccess: () => {
			setTarget(null)
			refresh()
		},
	})
	return {
		target,
		name,
		nameError,
		error: errorText(mutation.error, 'The key could not be renamed. Try again.'),
		isBusy: mutation.isPending,
		open: (key: ApiKeyDto) => {
			mutation.reset()
			setName(key.name)
			setNameError(null)
			setTarget(key)
		},
		cancel: () => setTarget(null),
		setName: (text: string) => {
			setName(text)
			setNameError(null)
		},
		submit: () => {
			const validName = parseApiKeyName(name)
			if (!target) return
			if (validName === undefined) {
				setNameError(NAME_INVALID)
				return
			}
			mutation.mutate({ apiKeyId: target.apiKeyId, name: validName })
		},
	}
}

/** The revoke confirmation: the key being revoked and its confirm. */
function useRevokeConfirm(refresh: () => void) {
	const [target, setTarget] = useState<ApiKeyDto | null>(null)
	const mutation = useMutation({
		mutationFn: (apiKeyId: string) => apiKeysService.revoke(apiKeyId),
		onSuccess: () => {
			setTarget(null)
			refresh()
		},
	})
	return {
		target,
		error: errorText(mutation.error, 'The key could not be revoked. Try again.'),
		isBusy: mutation.isPending,
		open: (key: ApiKeyDto) => {
			mutation.reset()
			setTarget(key)
		},
		cancel: () => setTarget(null),
		confirm: () => {
			if (target) mutation.mutate(target.apiKeyId)
		},
	}
}

/**
 * The Settings "API keys" section (TO-516 REQ-015): the signed-in user's keys
 * (`GET /users/me/api-keys`), create → reveal-once, rename and revoke.
 *
 * The created key's plaintext lives only in this hook's React state while the
 * reveal dialog is open, and is dropped on close. It is never put in the query
 * or mutation cache (every write refetches the list instead) nor in browser
 * storage.
 */
export function useApiKeys(enabled: boolean) {
	const client = useQueryClient()
	const list = useQuery({ queryKey: API_KEYS_QUERY_KEY, queryFn: () => apiKeysService.list(), enabled, retry: false })
	const refresh = () => void client.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })

	const [revealed, setRevealed] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)
	const create = useCreateDraft((plaintextKey) => {
		setCopied(false)
		setRevealed(plaintextKey)
	}, refresh)

	return {
		keys: list.data ?? [],
		isLoading: list.isPending,
		loadFailed: list.isError,
		reload: () => void list.refetch(),
		create,
		rename: useRenameDraft(refresh),
		revoke: useRevokeConfirm(refresh),
		reveal: {
			plaintextKey: revealed,
			copied,
			copy: () => {
				if (revealed === null) return
				void navigator.clipboard?.writeText(revealed).then(() => setCopied(true))
			},
			close: () => {
				setRevealed(null)
				setCopied(false)
			},
		},
	}
}
